import { useCallback } from 'react';
import { supabase } from '../lib/supabase';
/**
 * Hook for managing habit templates and instances
 * Handles cloning, skipping, and customizing steps/breaths per instance
 */
export function useHabitTemplates() {
    /**
     * Clone all template steps for a habit group for a specific date
     */
    const cloneTemplateSteps = useCallback(async (habitGroupId, instanceDate) => {
        try {
            // Fetch all template steps for this habit group
            const { data: templateSteps, error: fetchError } = await supabase
                .from('steps')
                .select('*')
                .eq('length_id', habitGroupId)
                .eq('is_template', true)
                .is('instance_date', null);
            if (fetchError)
                throw fetchError;
            if (!templateSteps || templateSteps.length === 0)
                return [];
            // Clone each template step
            const clonedSteps = [];
            for (const template of templateSteps) {
                const { data: clonedStep, error: insertError } = await supabase
                    .from('steps')
                    .insert({
                    user_id: template.user_id,
                    length_id: template.length_id,
                    label: template.label,
                    color: template.color,
                    duration: template.duration,
                    is_template: false,
                    template_step_id: template.id,
                    instance_date: instanceDate,
                    skipped_instances: [],
                })
                    .select()
                    .single();
                if (insertError)
                    throw insertError;
                if (clonedStep) {
                    clonedSteps.push(clonedStep);
                    // Also clone template breaths for this step
                    await cloneTemplateBreaths(template.id, clonedStep.id, instanceDate);
                }
            }
            // Create instance status records
            for (const step of clonedSteps) {
                const { error: statusError } = await supabase
                    .from('step_instance_status')
                    .insert({
                    step_id: step.id,
                    instance_date: instanceDate,
                    status: 'active',
                    user_id: step.user_id,
                });
                if (statusError)
                    console.warn('Failed to create step instance status:', statusError);
            }
            return clonedSteps;
        }
        catch (error) {
            console.error('Error cloning template steps:', error);
            throw error;
        }
    }, []);
    /**
     * Clone all template breaths for a step instance
     */
    const cloneTemplateBreaths = useCallback(async (templateStepId, newStepId, instanceDate) => {
        try {
            // Fetch all template breaths for this step
            const { data: templateBreaths, error: fetchError } = await supabase
                .from('breaths')
                .select('*')
                .eq('step_id', templateStepId)
                .eq('is_template', true)
                .is('instance_date', null);
            if (fetchError)
                throw fetchError;
            if (!templateBreaths || templateBreaths.length === 0)
                return [];
            const clonedBreaths = [];
            for (const template of templateBreaths) {
                const { data: clonedBreath, error: insertError } = await supabase
                    .from('breaths')
                    .insert({
                    step_id: newStepId,
                    user_id: template.user_id,
                    name: template.name,
                    is_template: false,
                    template_breath_id: template.id,
                    instance_date: instanceDate,
                    skipped_instances: [],
                    completed: false,
                    is_running: false,
                    start_time: null,
                    end_time: null,
                    paused_time: 0,
                    total_time_seconds: 0,
                    time_estimation_seconds: template.time_estimation_seconds || 0,
                    position: template.position || 0,
                })
                    .select()
                    .single();
                if (insertError)
                    throw insertError;
                if (clonedBreath) {
                    clonedBreaths.push(clonedBreath);
                    // Create breath instance status
                    const { error: statusError } = await supabase
                        .from('breath_instance_status')
                        .insert({
                        breath_id: clonedBreath.id,
                        instance_date: instanceDate,
                        status: 'active',
                        user_id: clonedBreath.user_id,
                    });
                    if (statusError)
                        console.warn('Failed to create breath instance status:', statusError);
                }
            }
            return clonedBreaths;
        }
        catch (error) {
            console.error('Error cloning template breaths:', error);
            throw error;
        }
    }, []);
    /**
     * Get all steps (template + instance) for a habit group
     */
    const getHabitSteps = useCallback(async (habitGroupId, instanceDate) => {
        try {
            let query = supabase.from('steps').select('*').eq('length_id', habitGroupId);
            if (instanceDate) {
                // Get steps for specific instance
                query = query.eq('instance_date', instanceDate);
            }
            else {
                // Get template steps
                query = query.eq('is_template', true).is('instance_date', null);
            }
            const { data, error } = await query;
            if (error)
                throw error;
            return (data || []);
        }
        catch (error) {
            console.error('Error fetching habit steps:', error);
            throw error;
        }
    }, []);
    /**
     * Skip an instance of a habit step
     */
    const skipInstance = useCallback(async (stepId, instanceDate, userId) => {
        try {
            const { error } = await supabase
                .from('step_instance_status')
                .update({ status: 'skipped' })
                .eq('step_id', stepId)
                .eq('instance_date', instanceDate)
                .eq('user_id', userId);
            if (error)
                throw error;
            // Also update the step's skipped_instances array
            const { data: step, error: fetchError } = await supabase
                .from('steps')
                .select('skipped_instances')
                .eq('id', stepId)
                .single();
            if (fetchError)
                throw fetchError;
            const skipped = step?.skipped_instances || [];
            if (!skipped.includes(instanceDate)) {
                skipped.push(instanceDate);
                await supabase
                    .from('steps')
                    .update({ skipped_instances: skipped })
                    .eq('id', stepId);
            }
        }
        catch (error) {
            console.error('Error skipping instance:', error);
            throw error;
        }
    }, []);
    /**
     * Duplicate a template step for a specific instance (customization)
     * Marks the template as skipped for this instance
     */
    const duplicateAndCustomize = useCallback(async (templateStepId, instanceDate, habitGroupId, userId) => {
        try {
            // Fetch the template
            const { data: template, error: fetchError } = await supabase
                .from('steps')
                .select('*')
                .eq('id', templateStepId)
                .single();
            if (fetchError || !template)
                throw fetchError || new Error('Template not found');
            // Create a new independent copy for this instance
            const { data: duplicated, error: insertError } = await supabase
                .from('steps')
                .insert({
                user_id: userId,
                length_id: habitGroupId,
                label: template.label,
                color: template.color,
                duration: template.duration,
                is_template: false,
                template_step_id: null, // Not linked to template anymore - it's customized
                instance_date: instanceDate,
                skipped_instances: [],
            })
                .select()
                .single();
            if (insertError || !duplicated)
                throw insertError || new Error('Failed to duplicate step');
            // Mark the template as skipped for this instance
            await skipInstance(templateStepId, instanceDate, userId);
            // Create instance status for the new step
            await supabase.from('step_instance_status').insert({
                step_id: duplicated.id,
                instance_date: instanceDate,
                status: 'duplicated',
                duplicated_from_id: templateStepId,
                user_id: userId,
            });
            return duplicated;
        }
        catch (error) {
            console.error('Error duplicating and customizing step:', error);
            throw error;
        }
    }, [skipInstance]);
    /**
     * Reset a customized step back to template defaults
     */
    const resetToTemplate = useCallback(async (stepId, instanceDate, userId) => {
        try {
            // Fetch the step
            const { data: step, error: fetchError } = await supabase
                .from('steps')
                .select('*')
                .eq('id', stepId)
                .single();
            if (fetchError || !step)
                throw fetchError || new Error('Step not found');
            if (!step.template_step_id) {
                console.warn('Step is not linked to a template');
                return;
            }
            // Fetch the template
            const { data: template, error: templateError } = await supabase
                .from('steps')
                .select('*')
                .eq('id', step.template_step_id)
                .single();
            if (templateError || !template)
                throw templateError || new Error('Template not found');
            // Update the step with template values
            await supabase
                .from('steps')
                .update({
                label: template.label,
                color: template.color,
                duration: template.duration,
            })
                .eq('id', stepId);
            // Update instance status
            await supabase
                .from('step_instance_status')
                .update({ status: 'active', duplicated_from_id: null })
                .eq('step_id', stepId)
                .eq('instance_date', instanceDate)
                .eq('user_id', userId);
        }
        catch (error) {
            console.error('Error resetting to template:', error);
            throw error;
        }
    }, []);
    /**
     * Get instance status for a step on a specific date
     */
    const getInstanceStatus = useCallback(async (stepId, instanceDate, userId) => {
        try {
            const { data, error } = await supabase
                .from('step_instance_status')
                .select('*')
                .eq('step_id', stepId)
                .eq('instance_date', instanceDate)
                .eq('user_id', userId)
                .single();
            if (error && error.code !== 'PGRST116')
                throw error; // PGRST116 = no rows
            return data || null;
        }
        catch (error) {
            console.error('Error fetching instance status:', error);
            return null;
        }
    }, []);
    return {
        cloneTemplateSteps,
        cloneTemplateBreaths,
        getHabitSteps,
        skipInstance,
        duplicateAndCustomize,
        resetToTemplate,
        getInstanceStatus,
    };
}
